import { SxProps, Theme } from "@mui/material";
import {
  assessmentFlowContainerStyle,
  assessmentPhoneLandscapeMedia,
  assessmentShortViewportMedia,
} from "../assessment-layout-styles";

export const containerStyle: SxProps<Theme> = assessmentFlowContainerStyle;

export const headerBoxStyle: SxProps<Theme> = {
  px: { xs: 2, sm: 4, md: 4 },
  pb: { xs: 1, sm: 2, md: 3 },
  pt: 0,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: { xs: "12px", sm: "12px", md: 3 },
  flexShrink: 0,
  [assessmentPhoneLandscapeMedia]: {
    px: 2,
    pb: 0.5,
    pt: 0,
    gap: "8px",
  },
  [assessmentShortViewportMedia]: {
    px: 1.5,
    pb: 0.5,
    pt: 0,
    gap: "6px",
  },
};

export const mobileTopPanelsStyle: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

export const mobileBottomLeaderboardStyle = (
  isPhoneLandscape: boolean,
  isLeaderboardExpanded: boolean,
  isShortViewport = false,
): SxProps<Theme> => {
  if (isPhoneLandscape) {
    return {
      display: isLeaderboardExpanded ? "flex" : "block",
      flexDirection: isLeaderboardExpanded ? "column" : undefined,
      justifyContent: isLeaderboardExpanded ? "flex-end" : undefined,
      flex: isLeaderboardExpanded ? "1 1 auto" : "0 0 auto",
      flexShrink: isLeaderboardExpanded ? 1 : 0,
      minHeight: isLeaderboardExpanded ? "min(40dvh, 220px)" : 0,
      maxHeight: isLeaderboardExpanded ? "min(58dvh, 285px)" : "none",
      overflow: "visible",
      px: 1.5,
      pb: 0.5,
      pt: 0,
      zIndex: isLeaderboardExpanded ? 2 : undefined,
    };
  }

  return {
    flex: "0 0 auto",
    flexShrink: 0,
    minHeight: 0,
    maxHeight: isLeaderboardExpanded
      ? isShortViewport
        ? "min(36dvh, 180px)"
        : "none"
      : isShortViewport
        ? "none"
        : "none",
    overflow: isLeaderboardExpanded
      ? "visible"
      : { xs: "visible", sm: "visible" },
    px: { xs: 2, sm: 4 },
    pb: { xs: isShortViewport ? 0.5 : 1, sm: 1.5 },
    pt: isShortViewport ? 1 : 0,
    [assessmentShortViewportMedia]: {
      px: 0,
      pb: 0.5,
      pt: 1,
    },
  };
};

export const closeButtonStyle: SxProps<Theme> = (theme) => ({
  position: "absolute",
  right: 20,
  top: 25,
  cursor: "pointer",
  color: theme.palette.quizPopap.quizeCross,
});

export const contentBoxStyle: SxProps<Theme> = {
  flex: "1 1 0",
  minHeight: { sm: 240, md: 320 },
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  px: { xs: 2, sm: 4, md: 6 },
  pb: 2,
  [assessmentShortViewportMedia]: {
    px: 1.5,
    pb: 1,
    minHeight: 120,
  },
};

export const footerBoxStyle = (
  theme: Theme,
  isEvaluating = false,
): SxProps<Theme> => ({
  flexShrink: 0,
  p: { xs: 1, sm: 1.25, md: 1.5, lg: 2 },
  textAlign: "right",
  borderTop: `1px solid ${theme.palette.quizPopap.quizSeparator}`,
  ...(isEvaluating ? { pointerEvents: "none" as const } : {}),
  [assessmentPhoneLandscapeMedia]: {
    p: "6px 12px",
  },
  [assessmentShortViewportMedia]: {
    p: "6px 10px",
  },
});

export const footerActionsStyle: SxProps<Theme> = {
  display: "flex",
  flexWrap: "nowrap",
  justifyContent: { xs: "space-between", sm: "flex-end" },
  alignItems: "center",
  gap: { xs: 0.5, sm: 0.875, md: 1.25, lg: 1.5 },
  width: "100%",
};

export const footerPrimaryActionsStyle: SxProps<Theme> = {
  display: "flex",
  flexWrap: "nowrap",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: { xs: 0.5, sm: 0.875, md: 1.25, lg: 1.5 },
  marginLeft: { xs: 0, sm: "auto" },
  flexShrink: 1,
  minWidth: 0,
};

export const buttonStyle = (
  theme: Theme,
  disabled: boolean
): SxProps<Theme> => ({
  backgroundColor: disabled ? "#9ED9E2" : "#06B6D4",
  color: "#fff",
  borderRadius: "9999px",
  textTransform: "none",
  fontWeight: 600,
  px: { xs: 0.75, sm: 1.25, md: 1.75, lg: 3 },
  py: { xs: 0.5, sm: 0.75, md: 1, lg: 1.5 },
  fontSize: { xs: "10px", sm: "12px", md: "13px", lg: "16px" },
  minHeight: { xs: 28, sm: 34, md: 38, lg: 44 },
  minWidth: 0,
  flexShrink: 1,
  whiteSpace: "nowrap",
  lineHeight: 1.2,
  ...(!disabled && {
    "&:hover": {
      backgroundColor: "#06B6D4",
      opacity: 0.9,
    },
  }),
  "&.Mui-disabled": {
    backgroundColor: "#9ED9E2",
    color: "#ffffff",
    opacity: 1,
  },
  [assessmentPhoneLandscapeMedia]: {
    px: 0.75,
    py: 0.4,
    fontSize: "10px",
    minHeight: 26,
  },
});

export const iconBoxStyle: SxProps<Theme> = {
  ml: { xs: 0.5, sm: 0.875, md: 1.25, lg: 2 },
  width: { xs: 14, sm: 18, md: 22, lg: 32 },
  height: { xs: 14, sm: 18, md: 22, lg: 32 },
  flexShrink: 0,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    width: { xs: "14px", sm: "18px", md: "22px", lg: "36px" },
    height: { xs: "14px", sm: "18px", md: "22px", lg: "36px" },
  },
  [assessmentPhoneLandscapeMedia]: {
    ml: 0.4,
    width: 12,
    height: 12,
    "& svg": {
      width: "12px",
      height: "12px",
    },
  },
};

export const previousIconBoxStyle: SxProps<Theme> = {
  ...iconBoxStyle,
  ml: 0,
  mr: { xs: 0.5, sm: 0.875, md: 1.25, lg: 2 },
  "& svg": {
    width: { xs: "14px", sm: "18px", md: "22px", lg: "36px" },
    height: { xs: "14px", sm: "18px", md: "22px", lg: "36px" },
    transform: "rotate(180deg)",
  },
  [assessmentPhoneLandscapeMedia]: {
    ml: 0,
    mr: 0.4,
    width: 12,
    height: 12,
    "& svg": {
      width: "12px",
      height: "12px",
      transform: "rotate(180deg)",
    },
  },
};

export const retryIconBoxStyle: SxProps<Theme> = {
  ...iconBoxStyle,
  "& svg": {
    width: { xs: "14px", sm: "18px", md: "22px", lg: "36px" },
    height: { xs: "14px", sm: "18px", md: "22px", lg: "36px" },
    transform: "rotate(-90deg)",
  },
  [assessmentPhoneLandscapeMedia]: {
    ml: 0.4,
    width: 12,
    height: 12,
    "& svg": {
      width: "12px",
      height: "12px",
      transform: "rotate(-90deg)",
    },
  },
};

